import { linkTo } from '@99designs/routing';
import type { ZendeskLanguage } from '@99designs/routing';
import type { ProductGroupName, QuestionAndAnswer } from '../types';

export const faqs = (key: ProductGroupName, lang: ZendeskLanguage): QuestionAndAnswer[] => {
    const faqs: Partial<Record<ProductGroupName, QuestionAndAnswer[]>> = {
        'logo-design-contest': [
            {
                question: __("Wait, what's a design contest?"),
                answer: __(
                    "Our contest method is where you create a brief that's open to our entire designer community. Designers pitch their concepts, and you can give feedback on these designs before selecting your favorite. At the end of the contest, you'll receive the copyright and files for your winning design."
                ),
            },
            {
                question: __('How does the 100% money-back guarantee work?'),
                answer: __(
                    "We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all logo design contests, except those with a <GuaranteedPrizeLink>guaranteed prize</GuaranteedPrizeLink> and those that have entered the <FinalRoundLink>Final Round</FinalRoundLink>. If you would like help re-writing your brief, amending your contest, giving feedback, or even getting advice on your entries, don't hesitate <ContactUsLink>to contact us</ContactUsLink> — we're here to help!"
                ),
                links: {
                    MoneyBackGuaranteeLink: `${linkTo.supportUrl('204108729', 'articles', lang)}`,
                    GuaranteedPrizeLink: `${linkTo.supportUrl('204761955', 'articles', lang)}`,
                    FinalRoundLink: `${linkTo.supportUrl('204109489', 'articles', lang)}`,
                    ContactUsLink: `${linkTo.supportUrl('204108739', 'articles', lang)}`,
                },
            },
            {
                question: __('What if I need something other than a logo?'),
                answer: __(
                    "We got you! If you'd like a logo plus another design, check out our logo bundles. Or, if you'd like something completely different, you can browse our <DesignCategoriesLink>full range of design categories</DesignCategoriesLink>. And if you've just finished a contest, you can talk to your winning designer about doing a new project."
                ),
                links: {
                    DesignCategoriesLink: '/categories',
                },
            },
            {
                question: __('Can I select multiple winners?'),
                answer: __(
                    "You sure can! If you're finding it hard to choose just one winner at the end of your contest, you can add <MultipleWinnersLink>multiple winning designs at a discounted rate</MultipleWinnersLink>."
                ),
                links: {
                    MultipleWinnersLink: `${linkTo.supportUrl('204760895', 'articles', lang)}`,
                },
            },
            {
                question: __('How many variations of my design do I get?'),
                answer: __(
                    "You'll receive a single design at the end of your contest, and this design can include a color variation such as color vs grayscale, a layout variation such as vertical vs horizontal, or simply an icon-only layout. If you require more substantial variations or edits, you can negotiate the price of these with your winning designer."
                ),
            },
            {
                question: __('How long does a contest take?'),
                answer: __(
                    "There are a total of 7 days within the Qualifying and Final rounds of your contest, after which you choose your winning design. If you'd like a faster turnaround, you can also run a <FastTrackedLink>fast-tracked contest</FastTrackedLink>."
                ),
                links: {
                    FastTrackedLink: `${linkTo.supportUrl('204760805', 'articles', lang)}`,
                },
            },
            {
                question: __('What happens after I pay for my contest?'),
                answer: __(
                    "Before you pay, you'll write your creative brief. This will give designers an idea of what kind of design you're looking for. After that, you'll pay for your contest, and then the real fun begins! Your contest goes out to our entire community, and designers will start submitting concepts over the next few days. You can provide feedback during this time to help refine these designs before selecting your finalists. View this article for a full overview of the <ContestTimelineLink>contest timeline</ContestTimelineLink>."
                ),
                links: {
                    ContestTimelineLink: `${linkTo.supportUrl('204109459', 'articles', lang)}`,
                },
            },
        ],
        'logo-brand-identity-pack-contest': [
            {
                question: __("Wait, what's a design contest?"),
                answer: __(
                    "Our contest method is where you create a brief that's open to our entire designer community. Designers pitch their concepts, and you can give feedback on these designs before selecting your favorite. At the end of the contest, you'll receive the copyright and files for your winning design."
                ),
            },
            {
                question: __('How does the 100% money-back guarantee work?'),
                answer: __(
                    "We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all logo & brand guide contests, except those with a <GuaranteedPrizeLink>guaranteed prize</GuaranteedPrizeLink> and those that have entered the <FinalRoundLink>Final Round</FinalRoundLink>. If you would like help re-writing your brief, amending your contest, giving feedback, or even getting advice on your entries, don't hesitate <ContactUsLink>to contact us</ContactUsLink> — we're here to help!"
                ),
                links: {
                    MoneyBackGuaranteeLink: `${linkTo.supportUrl('204108729', 'articles', lang)}`,
                    GuaranteedPrizeLink: `${linkTo.supportUrl('204761955', 'articles', lang)}`,
                    FinalRoundLink: `${linkTo.supportUrl('204109489', 'articles', lang)}`,
                    ContactUsLink: `${linkTo.supportUrl('204108739', 'articles', lang)}`,
                },
            },
            {
                question: __('How will I get the brand guide?'),
                answer: __(
                    "To keep the decision-making process as simple as possible (phew!), your logo & brand guide contest will focus on the logo design only. Once you've picked a winning logo you love, that designer will work with you to create your brand guide."
                ),
            },
            {
                question: __('What if I need something other than a logo?'),
                answer: __(
                    "We got you! If you'd like a logo plus another design, check out our logo bundles. Or, if you'd like something completely different, you can browse our <DesignCategoriesLink>full range of design categories</DesignCategoriesLink>. And if you've just finished a contest, you can talk to your winning designer about doing a new project."
                ),
                links: {
                    DesignCategoriesLink: '/categories',
                },
            },
            {
                question: __('How many variations of my design do I get?'),
                answer: __(
                    "You'll receive a single design at the end of your contest, and this design can include a color variation such as color vs grayscale, a layout variation such as vertical vs horizontal, or simply an icon-only layout. If you require more substantial variations or edits, you can negotiate the price of these with your winning designer."
                ),
            },
            {
                question: __('How long does a contest take?'),
                answer: __(
                    "There are a total of 7 days within the Qualifying and Final rounds of your contest, after which you choose your winning design. If you'd like a faster turnaround, you can also run a <FastTrackedLink>fast-tracked contest</FastTrackedLink>."
                ),
                links: {
                    FastTrackedLink: `${linkTo.supportUrl('204760805', 'articles', lang)}`,
                },
            },
        ],
        'logo-brand-guide-contest': [
            {
                question: __("Wait, what's a design contest?"),
                answer: __(
                    "Our contest method is where you create a brief that's open to our entire designer community. Designers pitch their concepts, and you can give feedback on these designs before selecting your favorite. At the end of the contest, you'll receive the copyright and files for your winning design."
                ),
            },
            {
                question: __('How does the 100% money-back guarantee work?'),
                answer: __(
                    "We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all logo & brand guide contests, except those with a <GuaranteedPrizeLink>guaranteed prize</GuaranteedPrizeLink> and those that have entered the <FinalRoundLink>Final Round</FinalRoundLink>. If you would like help re-writing your brief, amending your contest, giving feedback, or even getting advice on your entries, don't hesitate <ContactUsLink>to contact us</ContactUsLink> — we're here to help!"
                ),
                links: {
                    MoneyBackGuaranteeLink: `${linkTo.supportUrl('204108729', 'articles', lang)}`,
                    GuaranteedPrizeLink: `${linkTo.supportUrl('204761955', 'articles', lang)}`,
                    FinalRoundLink: `${linkTo.supportUrl('204109489', 'articles', lang)}`,
                    ContactUsLink: `${linkTo.supportUrl('204108739', 'articles', lang)}`,
                },
            },
            {
                question: __('How will I get the brand guide?'),
                answer: __(
                    "To keep the decision-making process as simple as possible (phew!), your logo & brand guide contest will focus on the logo design only. Once you've picked a winning logo you love, that designer will work with you to create your brand guide."
                ),
            },
            {
                question: __('What if I need something other than a logo?'),
                answer: __(
                    "We got you! If you'd like a logo plus another design, check out our logo bundles. Or, if you'd like something completely different, you can browse our <DesignCategoriesLink>full range of design categories</DesignCategoriesLink>. And if you've just finished a contest, you can talk to your winning designer about doing a new project."
                ),
                links: {
                    DesignCategoriesLink: '/categories',
                },
            },
            {
                question: __('How many variations of my design do I get?'),
                answer: __(
                    "You'll receive a single design at the end of your contest, and this design can include a color variation such as color vs grayscale, a layout variation such as vertical vs horizontal, or simply an icon-only layout. If you require more substantial variations or edits, you can negotiate the price of these with your winning designer."
                ),
            },
            {
                question: __('How long does a contest take?'),
                answer: __(
                    "There are a total of 7 days within the Qualifying and Final rounds of your contest, after which you choose your winning design. If you'd like a faster turnaround, you can also run a <FastTrackedLink>fast-tracked contest</FastTrackedLink>."
                ),
                links: {
                    FastTrackedLink: `${linkTo.supportUrl('204760805', 'articles', lang)}`,
                },
            },
        ],
        'logo-business-card-design-contest': [
            {
                question: __("Wait, what's a design contest?"),
                answer: __(
                    "Our contest method is where you create a brief that's open to our entire designer community. Designers pitch their concepts, and you can give feedback on these designs before selecting your favorite. At the end of the contest, you'll receive the copyright and files for your winning design."
                ),
            },
            {
                question: __('How does the 100% money-back guarantee work?'),
                answer: __(
                    "We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all logo & business card contests, except those with a <GuaranteedPrizeLink>guaranteed prize</GuaranteedPrizeLink> and those that have entered the <FinalRoundLink>Final Round</FinalRoundLink>. If you would like help re-writing your brief, amending your contest, giving feedback, or even getting advice on your entries, don't hesitate <ContactUsLink>to contact us</ContactUsLink> — we're here to help!"
                ),
                links: {
                    MoneyBackGuaranteeLink: `${linkTo.supportUrl('204108729', 'articles', lang)}`,
                    GuaranteedPrizeLink: `${linkTo.supportUrl('204761955', 'articles', lang)}`,
                    FinalRoundLink: `${linkTo.supportUrl('204109489', 'articles', lang)}`,
                    ContactUsLink: `${linkTo.supportUrl('204108739', 'articles', lang)}`,
                },
            },
            {
                question: __('How will I get the business card design?'),
                answer: __(
                    "To keep the decision-making process as simple as possible (phew!), your logo & business card contest will focus on the logo design only. Once you've picked a winning logo you love, that designer will work with you to create your business card design."
                ),
            },
            {
                question: __('What if I need something other than a logo?'),
                answer: __(
                    "We got you! If you'd like a logo plus another design, check out our logo bundles. Or, if you'd like something completely different, you can browse our <DesignCategoriesLink>full range of design categories</DesignCategoriesLink>. And if you've just finished a contest, you can talk to your winning designer about doing a new project."
                ),
                links: {
                    DesignCategoriesLink: '/categories',
                },
            },
            {
                question: __('How many variations of my design do I get?'),
                answer: __(
                    "You'll receive a single design at the end of your contest, and this design can include a color variation such as color vs grayscale, a layout variation such as vertical vs horizontal, or simply an icon-only layout. If you require more substantial variations or edits, you can negotiate the price of these with your winning designer."
                ),
            },
            {
                question: __('How long does a contest take?'),
                answer: __(
                    "There are a total of 7 days within the Qualifying and Final rounds of your contest, after which you choose your winning design. If you'd like a faster turnaround, you can also run a <FastTrackedLink>fast-tracked contest</FastTrackedLink>."
                ),
                links: {
                    FastTrackedLink: `${linkTo.supportUrl('204760805', 'articles', lang)}`,
                },
            },
        ],
        'logo-social-media-pack-contest': [
            {
                question: __("Wait, what's a design contest?"),
                answer: __(
                    "Our contest method is where you create a brief that's open to our entire designer community. Designers pitch their concepts, and you can give feedback on these designs before selecting your favorite. At the end of the contest, you'll receive the copyright and files for your winning design."
                ),
            },
            {
                question: __('How does the 100% money-back guarantee work?'),
                answer: __(
                    "We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all logo & social media pack contests, except those with a <GuaranteedPrizeLink>guaranteed prize</GuaranteedPrizeLink> and those that have entered the <FinalRoundLink>Final Round</FinalRoundLink>. If you would like help re-writing your brief, amending your contest, giving feedback, or even getting advice on your entries, don't hesitate <ContactUsLink>to contact us</ContactUsLink> — we're here to help!"
                ),
                links: {
                    MoneyBackGuaranteeLink: `${linkTo.supportUrl('204108729', 'articles', lang)}`,
                    GuaranteedPrizeLink: `${linkTo.supportUrl('204761955', 'articles', lang)}`,
                    FinalRoundLink: `${linkTo.supportUrl('204109489', 'articles', lang)}`,
                    ContactUsLink: `${linkTo.supportUrl('204108739', 'articles', lang)}`,
                },
            },
            {
                question: __('How will I get the social media designs?'),
                answer: __(
                    "To keep the decision-making process as simple as possible (phew!), your logo & social media pack contest will focus on the logo design only. Once you've picked a winning logo you love, that designer will work with you to create your social media pack."
                ),
            },
            {
                question: __('What if I need something other than a logo?'),
                answer: __(
                    "We got you! If you'd like a logo plus another design, check out our logo bundles. Or, if you'd like something completely different, you can browse our <DesignCategoriesLink>full range of design categories</DesignCategoriesLink>. And if you've just finished a contest, you can talk to your winning designer about doing a new project."
                ),
                links: {
                    DesignCategoriesLink: '/categories',
                },
            },
            {
                question: __('How many variations of my design do I get?'),
                answer: __(
                    "You'll receive a single design at the end of your contest, and this design can include a color variation such as color vs grayscale, a layout variation such as vertical vs horizontal, or simply an icon-only layout. If you require more substantial variations or edits, you can negotiate the price of these with your winning designer."
                ),
            },
            {
                question: __('How long does a contest take?'),
                answer: __(
                    "There are a total of 7 days within the Qualifying and Final rounds of your contest, after which you choose your winning design. If you'd like a faster turnaround, you can also run a <FastTrackedLink>fast-tracked contest</FastTrackedLink>."
                ),
                links: {
                    FastTrackedLink: `${linkTo.supportUrl('204760805', 'articles', lang)}`,
                },
            },
        ],
        'brand-guide-essential': [
            {
                question: __('What if I need additional pages or content for my brand guide?'),
                answer: __(
                    "If you'd like to include additional pages or content in your brand guide, we recommend specifying this in your brief and discussing it with your designer after you start your project. The designer can create a quote if additional payment is required."
                ),
            },
            {
                question: __('What format will my brand guide be delivered in?'),
                answer: __(
                    'Your brand guide will be created as a PDF document that you can share with your team, partners and other designers to ensure brand consistency.'
                ),
            },
            {
                question: __('Can I see an example of what the brand guide looks like?'),
                answer: __(
                    'Sure. Check out the brand guide produced for <BrandGuideExampleLink>Frugally Sustainable (PDF)</BrandGuideExampleLink>.'
                ),
                links: {
                    BrandGuideExampleLink:
                        'https://dmeu2ky7yoz4d.cloudfront.net/alchemy-attachments/2017/04/07/05/35/13/aae94df7-d1e2-4e1a-8385-0379a475286a/FrugallySustainableBrandGuidelines.pdf?_ga=2.187735848.1439331290.1670795086-1932148247.1633314285',
                },
            },
        ],
        'logo-design-contest-and-fixed-price': [
            {
                question: __placeholder(
                    "What's the difference between a 1-to-1 Project and a Contest?"
                ),
                answer: __placeholder(
                    "In a design Contest, designers from all over the world submit concepts unique to your brief. You choose finalists, give feedback to hone the designs and ultimately award your favorite design as the winner. If ideation and seeing a variety of different design styles is important to you, we'd recommend a design Contest. On the other hand, if you have a clear idea of the style and direction you'd like to take and would like to browse portfolios and collaborate closely with a single designer, we recommend choosing a 1-to-1 Project."
                ),
            },
            {
                question: __placeholder('How does the 100% money-back guarantee work?'),
                answer: __placeholder(
                    "We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all logo design contests, except those with a <GuaranteedPrizeLink>guaranteed prize</GuaranteedPrizeLink> and those that have entered the <FinalRoundLink>Final Round</FinalRoundLink>. If you would like help re-writing your brief, amending your contest, giving feedback, or even getting advice on your entries, don't hesitate <ContactUsLink>to contact us</ContactUsLink> — we're here to help! <RefundOneToOneLink>Refunds in 1-to-1 Projects</RefundOneToOneLink> need to be discussed and confirmed with your chosen designer."
                ),
                links: {
                    MoneyBackGuaranteeLink: `${linkTo.supportUrl('204108729', 'articles', lang)}`,
                    GuaranteedPrizeLink: `${linkTo.supportUrl('204761955', 'articles', lang)}`,
                    FinalRoundLink: `${linkTo.supportUrl('204109489', 'articles', lang)}`,
                    ContactUsLink: `${linkTo.supportUrl('204108739', 'articles', lang)}`,
                    RefundOneToOneLink: `${linkTo.supportUrl('204108679', 'articles', lang)}`,
                },
            },
            {
                question: __placeholder('What if I need something other than a logo?'),
                answer: __placeholder(
                    "We got you! If you'd like a logo plus another design, check out our <DesignCategoriesLink>logo bundles or browse our full range of design categories</DesignCategoriesLink>. If you've just finished a Contest or 1-to-1 Project, you can talk to your designer about doing a new project."
                ),
                links: {
                    DesignCategoriesLink: '/categories',
                },
            },
            {
                question: __placeholder('What is a design variation?'),
                answer: __placeholder(
                    "A logo design variation is like giving your primary logo a little makeover. It's a modified version of your original logo that retains the core elements of your brand while incorporating changes of colors or layouts. Logo design variations are often created to cater to specific use cases or applications to allow your logo to shine in all its forms. For example your logo might look better as a landscape layout for your website vs a horizontal layout for a t-shirt. You might also need different color combinations to cater for different use cases such as on a dark or light background, or even a season variation such as a spooky halloween or festive Christmas logo! If you require more substantial variations or edits, you can negotiate the price of these with your designer."
                ),
            },
            {
                question: __placeholder('What does Entry Level, Mid Level and Top Level mean?'),
                answer: __placeholder(
                    "We work hard to maintain the quality and trust of our design community. That's why we diligently curate all of our designers based on skill level, overall quality and professionalism. To reward designers as they build their skills, 99designs curates every member of our design community into one of three levels: Top Level refers to professionals that have built trust on 99designs with their expert skills and creativity. They have a professional network of happy repeat clients, an impressive portfolio and glowing feedback from the people they work with. Mid Level refers to designers who have more experience than entry-level designers but are not yet at the level of the most experienced and highly skilled designers. These designers have completed several projects and have received positive feedback from clients, but may still be refining their skills. Entry Level refers to designers who are new to the platform or have limited experience. These designers are still learning and building their portfolio but can still produce quality designs."
                ),
            },
            {
                question: __placeholder('Will I own the copyright to my design?'),
                answer: __placeholder(
                    "Absolutely! The <CopyrightOwnershipLink>copyright ownership</CopyrightOwnershipLink> of the final design will be transferred to you upon completion of the project or contest. This means that you will have the right to use, modify, and distribute the logo as you wish. You can trademark the design and use it for commercial purposes. The designer will retain the right to display the logo for promotional purposes in their portfolios, websites and other exhibitions unless you choose to run a <PrivateContestLink>private contest</PrivateContestLink> or specify in a 1-to-1 project brief that your designer cannot use the design in their portfolio. It's important to note that copyright ownership only applies to the final design that is approved and paid for by you. Any unused concepts or designs remain the property of the designer. <ContestCopyrightLink>Read more about Copyright in a design Contest here</ContestCopyrightLink>."
                ),
                links: {
                    CopyrightOwnershipLink: `${linkTo.supportUrl('204761105', 'articles', lang)}`,
                    PrivateContestLink: `${linkTo.supportUrl('204760795', 'articles', lang)}`,
                    ContestCopyrightLink: `${linkTo.supportUrl('204761095', 'articles', lang)}`,
                },
            },
            {
                question: __placeholder('What is an initial concept?'),
                answer: __placeholder(
                    "An initial design concept is the first draft or idea that a designer presents to you as part of your design project. It's the starting point for your design and represents the designer's interpretation of your brief and requirements. When you create a design project on 99designs, you provide a detailed brief outlining your design requirements, and any specific preferences you may have. Based on this brief, the designer creates an initial design concept that reflects their understanding of your needs."
                ),
            },
            {
                question: __placeholder('Can I work with my designer when my contest ends?'),
                answer: __placeholder(
                    'Absolutely! Finding a designer with whom you can build a long-term working relationship is the ultimate goal. When your logo design contest finishes, you can continue working directly with your designer on additional designs through 1-to-1 Projects.'
                ),
            },
            {
                question: __placeholder(
                    'I want to do a contest, which pricing package should I choose?'
                ),
                answer: __placeholder(
                    "Our design Contests are set up in such a way that you'll get a great logo design at any pricing level, but our higher-priced packages attract a higher level of designer participation. Here's how it works: When you pay more, the prize for the winning designer is bigger, too. Choosing Gold over Bronze means you'll get more submissions to choose from. Our Gold contests are restricted, so only hand-selected, Top- and Mid-Level designers can participate, meaning you'll be sure to get a top-quality logo."
                ),
            },
        ],
    };
    return faqs[key] || [];
};
